import LucidMascot from "@/assets/images/lucid-mascot.png";
import Layout from "@/components/elements/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { submitContactUs, verifyCaptcha } from "@/services/api/Master";
import { CircleNotch } from "@phosphor-icons/react";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";

import { useReCaptcha } from "next-recaptcha-v3";
import { getSEOByPathname } from "@/services/api/SEO";

export default function ContactUs({seoData}) {
  const router = useRouter();
  const [isLoading, setLoading] = useState(false);
  const [first_name, setFirstName] = useState("");
  const [last_name, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [job_title, setJobTitle] = useState("");
  const [company_name, setCompany] = useState("");
  const [phone, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");
  const [category_code, setCategoryCode] = useState("");

  const { executeRecaptcha } = useReCaptcha();

  const submit = useCallback(
    async (event) => {
      try {
        event.preventDefault();
        setLoading(true);
        const token = await executeRecaptcha("form_submit");
        const response = await verifyCaptcha(token);
        if (response.data.score > 0.5) {
          await submitContactUs({
            first_name,
            last_name,
            email,
            job_title,
            company_name,
            phone,
            message,
            category_code
          });
          toast.success("Your message has been submitted!");
          router.push("/");
        }
      } catch (error) {
        toast.error(
          "An error occured while sending the data. Please try again!"
        );
      } finally {
        setLoading(false);
      }
    },
    [
      executeRecaptcha,
      first_name,
      last_name,
      email,
      job_title,
      company_name,
      phone,
      message,
    ]
  );

  return (
    <Layout seoData={seoData} title={"Contact Support"}>
      <div className='container py-10 lg:py-16 lg:flex lg:space-x-24'>
        <div className='lg:flex-1'>
          <div className='ml-auto 2xl:max-w-lg'>
            <h1 className='text-5xl font-bold leading-none text-white lg:leading-tight lg:text-6xl font-display'>
              Contact & Support
            </h1>
            <h2 className='mt-4 text-sm lg:text-base text-white/60'>
              You can always let us know if you experience any issue when using
              our platform by filling in the form below.
            </h2>
            <form onSubmit={submit} className='grid grid-cols-12 gap-4 mt-6'>
              <div className='col-span-12 space-y-1.5 text-left'>
                <Label className='text-xs' htmlFor='category'>
                  Category
                </Label>
                <Select id='category' onValueChange={(value) => setCategoryCode(value)} required>
                  <SelectTrigger className='bg-white/5 hover:bg-white/10'>
                    <SelectValue placeholder='Select category ' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='media_inquiry'>Media Inquiry</SelectItem>
                    <SelectItem value='technical_issue'>
                      Technical Issue
                    </SelectItem>
                    <SelectItem value='billing_payment'>
                      Billing Payment and Refund
                    </SelectItem>
                    <SelectItem value='report_editorial'>
                      Report Editorial Error
                    </SelectItem>
                    <SelectItem value='sponsorship_partnership'>
                      Sponsorship and Partnership
                    </SelectItem>
                    <SelectItem value='other'>
                      Other
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className='col-span-12 py-3'>
                <hr />
              </div>
              <div className='col-span-6 space-y-1.5 text-left'>
                <Label className='text-xs' htmlFor='firstName'>
                  First Name
                </Label>
                <Input
                  type='text'
                  id='firstName'
                  required
                  onInput={(evt) => setFirstName(evt.target.value)}
                  className='bg-white/5 hover:bg-white/10'
                />
              </div>
              <div className='col-span-6 space-y-1.5 text-left'>
                <Label className='text-xs' htmlFor='lastName'>
                  Last Name
                </Label>
                <Input
                  type='text'
                  id='lastName'
                  required
                  onInput={(evt) => setLastName(evt.target.value)}
                  className='bg-white/5 hover:bg-white/10'
                />
              </div>
              <div className='col-span-6 space-y-1.5 text-left'>
                <Label className='text-xs' htmlFor='email'>
                  Email
                </Label>
                <Input
                  type='email'
                  id='email'
                  required
                  onInput={(evt) => setEmail(evt.target.value)}
                  className='bg-white/5 hover:bg-white/10'
                />
              </div>
              <div className='col-span-6 space-y-1.5 text-left'>
                <Label className='text-xs' htmlFor='phone_number'>
                  Phone
                </Label>
                <Input
                  type='text'
                  id='phone_number'
                  required
                  onInput={(evt) => setPhoneNumber(evt.target.value)}
                  className='bg-white/5 hover:bg-white/10'
                />
              </div>
              <div className='col-span-6 space-y-1.5 text-left'>
                <Label className='text-xs' htmlFor='job_title'>
                  Job Title
                </Label>
                <Input
                  type='text'
                  id='job_title'
                  required
                  onInput={(evt) => setJobTitle(evt.target.value)}
                  className='bg-white/5 hover:bg-white/10'
                />
              </div>
              <div className='col-span-6 space-y-1.5 text-left'>
                <Label className='text-xs' htmlFor='company_name'>
                  Company Name (Optional)
                </Label>
                <Input
                  type='text'
                  id='company_name'
                  onInput={(evt) => setCompany(evt.target.value)}
                  className='bg-white/5 hover:bg-white/10'
                />
              </div>
              <div className='col-span-12 space-y-1.5 text-left'>
                <Label className='text-xs' htmlFor='message'>
                  Message
                </Label>
                <Textarea
                  id='message'
                  rows={5}
                  required
                  onInput={(evt) => setMessage(evt.target.value)}
                  className='bg-white/5 hover:bg-white/10'
                />
              </div>
              <div className='flex flex-col items-center col-span-12 gap-4'>
                <Button
                  type='submit'
                  className='w-full text-white bg-gradient-to-r from-yellow-1 to-red-1'
                  disabled={isLoading}
                >
                  {isLoading && (
                    <CircleNotch
                      weight='bold'
                      className='mr-2 text-lg animate-spin'
                    />
                  )}
                  {isLoading ? "Processing..." : "Submit"}
                </Button>
              </div>
            </form>
          </div>
        </div>
        <div className='relative items-center justify-center flex-1 hidden translate-x-10 lg:flex'>
          <div className='relative w-48 2xl:w-56 aspect-[199/320]'>
            <Image
              src={LucidMascot.src}
              alt=''
              layout={"fill"}
              objectFit={"cover"}
            />
            <span className='-z-10 absolute border-2 border-[#FFB561] w-[460px] 2xl:w-[520px] h-[460px] 2xl:h-[520px] -translate-x-[70%] -translate-y-1/2 top-1/2 left-1/2 rounded-full'></span>
            <span className='-z-10 absolute bg-[#F6A74C] w-[420px] 2xl:w-[480px] h-[420px] 2xl:h-[480px] -translate-x-[70%] -translate-y-1/2 top-1/2 left-1/2 rounded-full'></span>
            <span className='-z-10 absolute bg-[#FFB561] w-[420px] 2xl:w-[480px] h-[420px] 2xl:h-[480px] -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 rounded-full'></span>
            <svg
              viewBox='0 0 1024 1024'
              className='absolute left-1/2 top-1/2 -translate-y-1/2 -z-10 h-[56rem] w-[56rem] -translate-x-1/2 [mask-image:radial-gradient(closest-side,white,transparent)]'
              aria-hidden='true'
            >
              <circle
                cx={512}
                cy={512}
                r={512}
                fill='url(#8d958450-c69f-4251-94bc-4e091a323369)'
                fillOpacity='0.4'
              />
              <defs>
                <radialGradient id='8d958450-c69f-4251-94bc-4e091a323369'>
                  <stop stopColor='#fdaf3a' />
                  <stop offset={1} stopColor='#fdaf3a' />
                </radialGradient>
              </defs>
            </svg>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export async function getServerSideProps() {

  let seoData = {};
  try {
    const seoResponse = await getSEOByPathname('contact-us');
    seoData = seoResponse.data;
  } catch (error) {
    console.error("Error fetching SEO data:", error);
  }

  return {
    props: {
      seoData
    },
  };
}
